import React from "react";
import colors from "../theme/colors";
import radius from "../theme/radius";
import shadows from "../theme/shadows";
import typography from "../theme/typography";

const variantStyles = {
  primary: {
    background: colors.buttonPrimary,
    color: colors.buttonText,
    border: `1px solid ${colors.buttonPrimary}`,
    boxShadow: shadows.card,
  },
  secondary: {
    background: "transparent",
    color: colors.primary,
    border: `1px solid ${colors.primary}`,
    boxShadow: "none",
  },
  ghost: {
    background: "transparent",
    color: colors.primary,
    border: "1px solid transparent",
    boxShadow: "none",
  },
  soft: {
    background: colors.highlight,
    color: colors.primary,
    border: `1px solid ${colors.border}`,
    boxShadow: "none",
  },
  outlineSoft: {
    background: colors.surface,
    color: colors.primaryDark,
    border: `1px solid ${colors.borderSoft}`,
    boxShadow: "none",
  },
};

const sizeStyles = {
  xs: {
    minHeight: 34,
    padding: "0 12px",
    fontSize: typography.size.xs,
    lineHeight: "14px",
    borderRadius: radius.pill,
  },
  sm: {
    minHeight: 40,
    padding: "0 14px",
    fontSize: typography.size.sm,
    lineHeight: typography.lineHeight.sm,
    borderRadius: radius.lg,
  },
  md: {
    minHeight: 46,
    padding: "0 16px",
    fontSize: typography.size.sm,
    lineHeight: typography.lineHeight.sm,
    borderRadius: radius.lg,
  },
  lg: {
    minHeight: 52,
    padding: "0 20px",
    fontSize: typography.size.base,
    lineHeight: typography.lineHeight.base,
    borderRadius: radius.lg,
  },
};

function Spinner() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: "2px solid currentColor",
        borderRightColor: "transparent",
        display: "inline-block",
        animation: "mariam-button-spin 0.7s linear infinite",
      }}
    />
  );
}

export default function Button({
  children,
  type = "button",
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  fullWidth = false,
  align = "center",
  leftIcon = null,
  rightIcon = null,
  minWidth,
  style = {},
  className = "",
  onClick,
  as: Component = "button",
  ...rest
}) {
  const resolvedVariant = variantStyles[variant] || variantStyles.primary;
  const resolvedSize = sizeStyles[size] || sizeStyles.md;
  const isButtonElement = Component === "button";
  const isDisabled = disabled || loading;

  const handleClick = (event) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes mariam-button-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <Component
        type={isButtonElement ? type : undefined}
        className={className}
        disabled={isButtonElement ? isDisabled : undefined}
        aria-disabled={isDisabled ? true : undefined}
        onClick={handleClick}
        style={{
          minHeight: resolvedSize.minHeight,
          minWidth: minWidth ?? undefined,
          width: fullWidth ? "100%" : "auto",
          padding: resolvedSize.padding,
          borderRadius: resolvedSize.borderRadius,
          border: resolvedVariant.border,
          background: resolvedVariant.background,
          color: resolvedVariant.color,
          boxShadow: resolvedVariant.boxShadow,
          display: "inline-flex",
          alignItems: "center",
          justifyContent:
            align === "between"
              ? "space-between"
              : align === "left"
              ? "flex-start"
              : align === "right"
              ? "flex-end"
              : "center",
          gap: 8,
          fontFamily: typography.fontFamily,
          fontSize: resolvedSize.fontSize,
          fontWeight: typography.weight.bold,
          lineHeight: resolvedSize.lineHeight,
          textDecoration: "none",
          whiteSpace: "nowrap",
          textAlign: "center",
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.6 : 1,
          transition:
            "background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease",
          outline: "none",
          boxSizing: "border-box",
          userSelect: "none",
          flexShrink: 0,
          WebkitTapHighlightColor: "transparent",
          ...style,
        }}
        {...(!isButtonElement && isDisabled ? { tabIndex: -1 } : {})}
        {...rest}
      >
        {loading ? (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <Spinner />
          </span>
        ) : leftIcon ? (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {leftIcon}
          </span>
        ) : null}

        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {loading ? "Carregando..." : children}
        </span>

        {!loading && rightIcon ? (
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {rightIcon}
          </span>
        ) : null}
      </Component>
    </>
  );
}